import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { vetAPI, publicAPI, favoriteAPI } from '@/services/api';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ImageViewer from "@/components/ImageViewer";
import BackLink from "@/components/BackLink";
import LocationViewer from "@/components/LocationViewer";
import StickyInfoCard from "@/components/StickyInfoCard";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function VetDetailPage() {
	const { id } = useParams();
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [ad, setAd] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isFav, setIsFav] = useState(false);
	const [pricing, setPricing] = useState({
		free_images_limit: 5, price_extra_image: 0.70,
		free_videos_limit: 1, price_extra_video: 3.00,
		max_video_size_mb: 50, price_featured_ad: 10.00,
		vat_percent: 23
	});

	const API_BASE = process.env.REACT_APP_BACKEND_URL;

	useDocumentMeta({
		title: ad ? `${ad.title} | Equi-Market` : 'Equi-Market',
		description: ad?.description?.slice(0, 200),
		url: ad ? `${window.location.origin}/vets/${ad.slug || ad.id}` : '',
		price: ad?.price,
		currency: 'EUR',
		type: 'vet',
	});

	useEffect(() => {
		setLoading(true);
		publicAPI
			.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		vetAPI.get(id).then((r) => {
			setAd(r.data);
			if (user) {
				favoriteAPI
					.check(r.data.id)
					.then(f => setIsFav(f.data.is_favorite))
					.catch(() => {});
			}
		})
			.catch(() => navigate('/vets'))
			.finally(() => setLoading(false));
	}, [id, user, navigate]);

	if (loading || !ad) return (
		<div className="pt-24 min-h-screen flex items-center justify-center">
			<div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
		</div>
	);

	const hiddenImages = ad.hidden_images || [];
	const images = (ad.images || []).filter(url => !hiddenImages.includes(url)).map((url, i) => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null
	}));
	const hasLocation = ad.location_lat && ad.location_lng;

	const predefined = ad.predefined_services || [];
	const servicePrices = ad.service_prices || {};
	const custom = ad.custom_services || [];
	const specialties = ad.specialties || [];

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid={`vet-detail-${ad.id}`}>
			<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

				{/* Breadcrumb */}
				<BackLink title={ad.title} uri="/vets" textKey='nav.vets' />

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="lg:col-span-2 space-y-6">

						{/* Main Image */}
						<ImageViewer images={images} title={ad.title} is_featured={ad.is_featured} />

						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
							<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
								{t('horse.description')}
							</h2>
							<p className="text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line">{ad.description}</p>
						</div>

						{specialties.length > 0 && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{t('vet.specialties')}
								</h2>
								<div className="flex flex-wrap gap-2">
									{specialties.map(s => (
										<span key={s}
													className="text-xs bg-[#F2F0E9] text-[#1A3C34] px-3 py-1.5 rounded-sm border border-[#E6E2D6]"
													data-testid={`vet-specialty-tag-${s}`}>
											{t(`vet.specialty.${s}`)}
										</span>
									))}
								</div>
							</div>
						)}

						{(predefined.length > 0 || custom.length > 0) && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{t('vet.services_offered')}
								</h2>
								<div className="overflow-hidden rounded-sm border border-[#E6E2D6]" data-testid="vet-services-table">
									<table className="w-full text-sm">
										<tbody>
										{predefined.map(k => (
											<tr key={k} className="border-b border-[#F2F0E9] last:border-0">
												<td className="px-4 py-2.5 text-[#3a3a3a]">{t(`vet.service.${k}`)}</td>
												<td className="px-4 py-2.5 text-right font-semibold text-[#1A3C34] w-40"
														data-testid={`vet-service-row-${k}`}>
													{ servicePrices[k] != null ? `€${Number(servicePrices[k]).toLocaleString()}` :
														<span className="text-[#666055] italic font-normal">{t('vet.price_on_request')}</span>
													}
												</td>
											</tr>
										))}
										{custom.map((s, i) => (
											<tr key={`c-${i}`} className="border-b border-[#F2F0E9] last:border-0 bg-[#FAF9F5]/40">
												<td className="px-4 py-2.5 text-[#3a3a3a]">
													{s.name}
													{s.unit && s.unit !== 'visit' &&
														<span className="text-[10px] text-[#666055] ml-2 uppercase tracking-wider">
															/ {t(`vet.unit.${s.unit}`)}
														</span>
													}
												</td>
												<td className="px-4 py-2.5 text-right font-semibold text-[#1A3C34] w-40">
													{s.price != null ? `€${Number(s.price).toLocaleString()}` :
														<span className="text-[#666055] italic font-normal">{t('vet.price_on_request')}</span>
													}
												</td>
											</tr>
										))}
										</tbody>
									</table>
								</div>
							</div>
						)}
						{/* Map */ }
						{ hasLocation &&
							<LocationViewer title={ ad.title }
															location_name={ ad.location_name }
															location_lat={ ad.location_lat }
															location_lng={ ad.location_lng }/>
						}
					</div>

					{/* Right - Sticky Info */}
					<StickyInfoCard ad={ad}
													adType="vet"
													is_favorite={isFav}
													pricing={pricing} />

				</div>
			</div>
		</div>
	);
}